// HomeCoverMessage.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { ButtonId } from '../resource/mixpanel.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import withViewportItemTracker from '../resource/withViewportItemTracker.js';
import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import LogoLoading from '../component/LogoLoading.jsx';
import LazyImage, { Image } from '../component/LazyImage.jsx';
import StatefulImage from '../component/StatefulImage.jsx';
import DecryptionWrapper from '../container/DecryptionWrapper.js';
import { withSentryEndSpan } from '../resource/withSentrySpan.js';
import {
  color,
  textColor,
  showcasePlayerMobileHeight,
  showcasePlayerTabletHeight,
  showcasePlayerDesktopHeight,
} from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';
import { formatTimeString } from '../resource/formatTimeString.js';
import emptyShowCase from '../../img/empty_showcase.svg';
import unlockIcon from '../../img/ic-unlock.svg';
import likeIcon from '../../img/ic-like.svg';
import diamondIcon from '../../img/ic_diamond_sm.svg';
import playIcon from '../../img/ic_play_black.svg';
import bluetickIcon from '../../img/ic_pro_badge.svg';
import viewIcon from '../../img/ic_streaming_view_xs.svg';
import FlixBadge from '../container/FlixBadge.js';

export class HomeCoverMessage extends React.PureComponent {
  constructor(props) {
    super(props);

    const { thumbnailSource, thumbnailSourceBlurred } = this.props;
    const [avifSrcSet, webpSrcSet, jpgSrcSet] = this.calculateSrcSet({
      href: thumbnailSource,
      size: 1024,
    });
    const [avifSrcSetFallback, webpSrcSetFallback, jpgSrcSetFallback] =
      this.calculateSrcSet({
        href: thumbnailSource,
        size: 512,
      });
    const [unverifiedAvifSrcSet, unverifiedWebpSrcSet, unverifiedJpgSrcSet] =
      this.calculateSrcSet({
        href: thumbnailSourceBlurred,
        size: 1024,
      });
    const [
      unverifiedAvifSrcSetFallback,
      unverifiedWebpSrcSetFallback,
      unverifiedJpgSrcSetFallback,
    ] = this.calculateSrcSet({
      href: thumbnailSourceBlurred,
      size: 512,
    });

    this.state = {
      avifSrcSet,
      webpSrcSet,
      jpgSrcSet,
      avifSrcSetFallback,
      webpSrcSetFallback,
      jpgSrcSetFallback,
      unverifiedAvifSrcSet,
      unverifiedWebpSrcSet,
      unverifiedJpgSrcSet,
      unverifiedAvifSrcSetFallback,
      unverifiedWebpSrcSetFallback,
      unverifiedJpgSrcSetFallback,
    };
  }
  calculateSrcSet = ({ href, size }) =>
    [MediaAssetFormat.AVIF, MediaAssetFormat.WEBP, MediaAssetFormat.JPG].map(
      format => getPublicSrcSet({ href, size, format })
    );
  nextTick = null;
  player = null;
  renderPlaceholder = () => {
    const { shouldShowUnlockCount } = this.props;
    return (
      <StyledHomeCoverMessage>
        <PlayerBlock>
          <SwagLoadingWrapper>
            <LogoLoading />
          </SwagLoadingWrapper>
        </PlayerBlock>
        <CaptionWrapper isEmpty>
          <Caption></Caption>
        </CaptionWrapper>
        <MetaWrapper>
          <Meta isLeft>
            <MetaIcon
              src={!shouldShowUnlockCount ? viewIcon : unlockIcon}
              alt={
                !shouldShowUnlockCount ? 'amount of views' : 'amount of unlocks'
              }
            />
            <Amount>-</Amount>
          </Meta>
          <ActionButtonWrapper />
          <Meta>
            <MetaIcon src={likeIcon} alt="likes ratio" />
            <Amount>-</Amount>
          </Meta>
        </MetaWrapper>
      </StyledHomeCoverMessage>
    );
  };

  // (2024.03)
  // 1. No srcset for the Showcase (no 2x actually; there is an issue with 2048px)
  // 2. The order:
  // Verified: 1024 -> 512 -> blurred 256
  // Unverified: blurred 1024 -> blurred 512 -> blurred 256
  renderThumbnail = () => {
    const {
      username,
      originalTitle,
      originalText,
      hasVerified,
      shouldUseLazyImage,
    } = this.props;
    const {
      avifSrcSet,
      webpSrcSet,
      jpgSrcSet,
      avifSrcSetFallback,
      webpSrcSetFallback,
      jpgSrcSetFallback,
      unverifiedAvifSrcSet,
      unverifiedWebpSrcSet,
      unverifiedJpgSrcSet,
      unverifiedAvifSrcSetFallback,
      unverifiedWebpSrcSetFallback,
      unverifiedJpgSrcSetFallback,
    } = this.state;
    const altText = originalTitle || originalText;

    let targetAvifSrcSet = unverifiedAvifSrcSet;
    let targetWebpSrcSet = unverifiedWebpSrcSet;
    let targetJpgSrcSet = unverifiedJpgSrcSet;
    let errorAvifSrcSet = unverifiedAvifSrcSetFallback;
    let errorWebpSrcSet = unverifiedWebpSrcSetFallback;
    let errorJpgSrcSet = unverifiedJpgSrcSetFallback;

    if (hasVerified) {
      targetAvifSrcSet = avifSrcSet;
      targetWebpSrcSet = webpSrcSet;
      targetJpgSrcSet = jpgSrcSet;
      errorAvifSrcSet = avifSrcSetFallback;
      errorWebpSrcSet = webpSrcSetFallback;
      errorJpgSrcSet = jpgSrcSetFallback;
    }

    return (
      <Thumbnail>
        <StatefulImage>
          {/* for SSR, use Picture directly */}
          {shouldUseLazyImage ? (
            <LazyImage
              data-key="target"
              threshold={0.01}
              onLoad={this.handleTargetOnLoad}
            >
              <DecryptionWrapper resourceUrl={targetJpgSrcSet.src}>
                <source srcSet={targetAvifSrcSet.src} type="image/avif" />
                <source srcSet={targetWebpSrcSet.src} type="image/webp" />
                <Image {...targetJpgSrcSet} alt={`${username} : ${altText}`} />
              </DecryptionWrapper>
            </LazyImage>
          ) : (
            <Picture data-key="target" onLoad={this.handleTargetOnLoad}>
              <DecryptionWrapper resourceUrl={targetJpgSrcSet.src}>
                <source srcSet={targetAvifSrcSet.src} type="image/avif" />
                <source srcSet={targetWebpSrcSet.src} type="image/webp" />
                <img {...targetJpgSrcSet} alt={altText} />
              </DecryptionWrapper>
            </Picture>
          )}

          <LazyImage data-key="error" threshold={0.01}>
            <DecryptionWrapper resourceUrl={errorJpgSrcSet.src}>
              <source srcSet={errorAvifSrcSet.src} type="image/avif" />
              <source srcSet={errorWebpSrcSet.src} type="image/webp" />
              <Image src={errorJpgSrcSet.src} />
            </DecryptionWrapper>
          </LazyImage>

          <LazyImage data-key="error" threshold={0.01}>
            <Image src={emptyShowCase} alt="swag logo" />
          </LazyImage>
        </StatefulImage>
      </Thumbnail>
    );
  };

  getTrackingPayload = () => {
    const { messageId, authorId, unlockPrice, username, index, category } =
      this.props;
    return {
      messageId,
      abData: { unlockPrice },
      'user.id': authorId,
      'user.username': username,
      'discover.index': index,
      'discover.category': category,
    };
  };

  renderActionButton = () => {
    const { isUnlocked, messageId, t, unlockPrice } = this.props;
    const hasMessageId = !!messageId;
    const LinkProps = {
      as: isUnlocked ? PlayActionButton : UnlockActionButton,
      to: `/post/${messageId}`,
      'data-element_id': ButtonId.All.ButtonFreeWatch,
      'data-tracking_payload': this.getTrackingPayload(),
    };
    return (
      <ActionButtonWrapper>
        {/* $isUnlocked is a transient prop https://styled-components.com/docs/api#transient-props */}
        <ActionButton
          hasMessageId={hasMessageId}
          $isUnlocked={isUnlocked}
          {...(hasMessageId ? LinkProps : {})}
        >
          {isUnlocked ? (
            <>
              <PlayIcon src={playIcon} alt="play the video" />
              {t('button_free_watch', { ns: TranslationNamespace.FEED })}
            </>
          ) : (
            <>
              {t('message_detail_unlock', { ns: TranslationNamespace.FEED })}
              <DiamondIcon src={diamondIcon} alt="diamond" />
              <UnlockPrice>{unlockPrice}</UnlockPrice>
            </>
          )}
        </ActionButton>
      </ActionButtonWrapper>
    );
  };
  handleClickMessage = event => {
    const {
      isActive,
      isUIInteractable,
      isFlix,
      messageId,
      push,
      showcaseFeed,
    } = this.props;
    if (isActive && isUIInteractable) {
      event.stopPropagation();
      if (isFlix) {
        return push(`/post/${messageId}`);
      }

      return push(`/story/${messageId}`, {
        listPath: ['home', 'messages', showcaseFeed],
      });
    }
  };

  handleTargetOnLoad = () => {
    const { endSpan, category } = this.props;
    endSpan({ category });
  };

  componentDidMount() {
    this.nextTick = setTimeout(() => {
      const {
        messageId,
        username,
        authorId,
        isUnlocked,
        fetchMessageDetail,
        fetchDrmLicenseAvailability,
        fetchUser,
        fetchAssetManifest,
        duration,
        assetId,
      } = this.props;
      if (!username && authorId) {
        fetchUser({ authorId });
      }
      if (messageId) {
        fetchMessageDetail();
        if (!isUnlocked) {
          fetchDrmLicenseAvailability();
        }
        if (!duration && assetId) {
          fetchAssetManifest({ messageId, assetId });
        }
      }
    }, 0);
  }

  componentDidUpdate(prevProps) {
    const {
      isActive,
      isUIInteractable,
      messageId,
      isAuthed,
      isUnlocked,
      fetchDrmLicenseAvailability,
      thumbnailSource,
      thumbnailSourceBlurred,
    } = this.props;
    if (
      isActive &&
      isUIInteractable !== prevProps.isUIInteractable &&
      this.canPlayThrough
    ) {
      return isUIInteractable ? this.player.play() : this.player.pause();
    }
    if (messageId && isAuthed !== prevProps.isAuthed && !isUnlocked) {
      fetchDrmLicenseAvailability();
    }
    if (thumbnailSource !== prevProps.thumbnailSource) {
      const [avifSrcSet, webpSrcSet, jpgSrcSet] = this.calculateSrcSet({
        href: thumbnailSource,
        size: 1024,
      });
      const [avifSrcSetFallback, webpSrcSetFallback, jpgSrcSetFallback] =
        this.calculateSrcSet({
          href: thumbnailSource,
          size: 512,
        });
      this.setState({
        avifSrcSet,
        webpSrcSet,
        jpgSrcSet,
        avifSrcSetFallback,
        webpSrcSetFallback,
        jpgSrcSetFallback,
      });
    }
    if (thumbnailSourceBlurred !== prevProps.thumbnailSourceBlurred) {
      const [unverifiedAvifSrcSet, unverifiedWebpSrcSet, unverifiedJpgSrcSet] =
        this.calculateSrcSet({
          href: thumbnailSourceBlurred,
          size: 1024,
        });
      const [
        unverifiedAvifSrcSetFallback,
        unverifiedWebpSrcSetFallback,
        unverifiedJpgSrcSetFallback,
      ] = this.calculateSrcSet({
        href: thumbnailSourceBlurred,
        size: 512,
      });
      this.setState({
        unverifiedAvifSrcSet,
        unverifiedWebpSrcSet,
        unverifiedJpgSrcSet,
        unverifiedAvifSrcSetFallback,
        unverifiedWebpSrcSetFallback,
        unverifiedJpgSrcSetFallback,
      });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.nextTick);
  }

  renderBadge = () => {
    const { badges, messageBadgeNotShowList } = this.props;
    return (
      <BadgeWrapper>
        {badges
          .filter(value => !messageBadgeNotShowList.includes(value))
          .map(value => (
            <FlixBadge key={value} badgeName={value} />
          ))}
      </BadgeWrapper>
    );
  };

  render() {
    const {
      messageId,
      unlockTimes,
      viewTimes,
      likeRatio,
      unlockPrice,
      duration,
      username,
      isActive,
      isFlix,
      isPro,
      isUIInteractable,
      shouldShowUnlockCount,
      t,
      originalTitle,
      originalText,
    } = this.props;

    if (!messageId) {
      return this.renderPlaceholder();
    }

    let messageTitle = originalTitle || originalText;

    const isFreeMessage = unlockPrice === 0;
    const isMessageTitleEmpty = !messageTitle;

    if (isMessageTitleEmpty) {
      if (isFlix) {
        messageTitle = t('flix_caption_placeholder', {
          username,
          ns: TranslationNamespace.POST,
        });
      } else {
        messageTitle = t('message_caption_placeholder', {
          username,
          ns: TranslationNamespace.POST,
        });
      }
    }

    return (
      <StyledHomeCoverMessage
        isActive={isActive}
        data-click_tracking_disabled={
          isActive && isUIInteractable ? false : true
        }
        data-element_id={
          isFlix ? ButtonId.All.ButtonFlixCard : ButtonId.All.ButtonStoryCard
        }
        data-tracking_payload={this.getTrackingPayload()}
        onClick={this.handleClickMessage}
      >
        {this.renderBadge()}
        <PlayerBlock>
          {this.renderThumbnail()}
          {duration && <Duration>{formatTimeString(duration * 1000)}</Duration>}
          {/* To overlay on video player */}
          <Background />
        </PlayerBlock>
        <CaptionWrapper isEmpty={isMessageTitleEmpty}>
          {isPro && <ProIcon src={bluetickIcon} alt="pro icon" />}
          <Caption>{messageTitle}</Caption>
        </CaptionWrapper>
        <MetaWrapper>
          <Meta isLeft>
            {(!shouldShowUnlockCount || !isFreeMessage) && (
              <>
                <MetaIcon
                  src={!shouldShowUnlockCount ? viewIcon : unlockIcon}
                  alt={
                    !shouldShowUnlockCount
                      ? 'amount of views'
                      : 'amount of unlocks'
                  }
                />
                <Amount>
                  {!shouldShowUnlockCount ? viewTimes : unlockTimes}
                </Amount>
              </>
            )}
          </Meta>
          {this.renderActionButton()}
          <Meta>
            {!isFreeMessage && (
              <>
                <MetaIcon src={likeIcon} alt="likes ratio" />
                <Amount>{likeRatio}</Amount>
              </>
            )}
          </Meta>
        </MetaWrapper>
      </StyledHomeCoverMessage>
    );
  }
}

HomeCoverMessage.propTypes = {
  t: PropTypes.func.isRequired,
  index: PropTypes.number,
  category: PropTypes.string,
  authorId: PropTypes.string,
  messageId: PropTypes.string,
  assetId: PropTypes.string,
  username: PropTypes.string,
  thumbnailSource: PropTypes.string,
  thumbnailSourceBlurred: PropTypes.string,
  originalTitle: PropTypes.string,
  originalText: PropTypes.string,
  sendUnlockMessage: PropTypes.func,
  fetchDrmLicenseAvailability: PropTypes.func,
  fetchAssetManifest: PropTypes.func,
  fetchUser: PropTypes.func,
  push: PropTypes.func,
  fetchMessageDetail: PropTypes.func,
  unlockPrice: PropTypes.number,
  duration: PropTypes.number,
  unlockTimes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  viewTimes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  likeRatio: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isUnlocked: PropTypes.bool,
  isActive: PropTypes.bool,
  isUIInteractable: PropTypes.bool,
  isFlix: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isPro: PropTypes.bool,
  shouldShowUnlockCount: PropTypes.bool,
  hasVerified: PropTypes.bool,
  showcaseFeed: PropTypes.string,
  badges: PropTypes.array,
  messageBadgeNotShowList: PropTypes.array,
  unlockButtonStatus: PropTypes.string,
  shouldUseLazyImage: PropTypes.bool,
  endSpan: PropTypes.func,
};

HomeCoverMessage.defaultProps = {
  index: null,
  category: null,
  messageId: null,
  assetId: null,
  authorId: null,
  unlockPrice: 0,
  duration: 0,
  unlockTimes: '-',
  viewTimes: '-',
  likeRatio: '-',
  username: '',
  thumbnailSource: '',
  thumbnailSourceBlurred: '',
  originalTitle: '-',
  originalText: '-',
  isUnlocked: false,
  isActive: false,
  isUIInteractable: false,
  isFlix: false,
  isAuthed: false,
  unlockButtonStatus: '',
  hasVerified: false,
  sendUnlockMessage: () => null,
  fetchDrmLicenseAvailability: () => null,
  fetchAssetManifest: () => null,
  fetchUser: () => null,
  push: () => null,
  fetchMessageDetail: () => null,
  badges: [],
  messageBadgeNotShowList: [],
  isPro: false,
  shouldShowUnlockCount: false,
  showcaseFeed: '',
  shouldUseLazyImage: true,
  endSpan: () => null,
};

const borderRadius = 4;

const StyledHomeCoverMessage = styled.article.attrs(({ isActive }) => ({
  style: {
    opacity: isActive ? 1 : 0.5,
    transition: 'opacity 0.6s',
  },
}))`
  border-radius: ${borderRadius}px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  flex: none;
  overflow: hidden;
  position: relative;
`;

const Duration = styled.div`
  position: absolute;
  top: 16px;
  right: 20px;
  padding: 4px 8px;
  border-radius: 2px;
  background-color: rgba(25, 25, 25, 0.6);
  color: ${textColor.white};
  font-size: 16px;
  font-weight: 400;

  ${media.tablet`
    top: 12px;
    right: 12px;
    padding: 4px;
    font-size: 12px;
  `}
`;

const PlayerBlock = styled.div`
  background: rgba(100, 100, 100, 0.2);
  width: 100%;
  height: ${showcasePlayerDesktopHeight}px;
  position: relative;

  ${media.tablet`
    height: ${showcasePlayerTabletHeight}px;
  `}
  ${media.mobile`
    height: ${showcasePlayerMobileHeight}px;
  `}
`;

const SwagLoadingWrapper = styled.div`
  width: 120px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate3d(-50%, -50%, 0);
`;

const Thumbnail = styled.div`
  border-top-left-radius: ${borderRadius}px;
  border-top-right-radius: ${borderRadius}px;
  /* Make size a little smaller to fix pixel glitch of video (video can't be exact 100% width) */
  position: absolute;
  left: 1px;
  width: calc(100% - 2px);
  height: 100%;
`;

const BadgeWrapper = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  z-index: 1;
`;

const CaptionWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 0px 80px;
  gap: 4px;

  position: absolute;
  left: 0px;
  right: 0px;
  bottom: 72px;
  ${media.tablet`
    padding: 0px 12px;
    bottom: 64px;
  `};
  ${media.mobile`
    width: 320px;
    height: 19px;
    top: 157px;
    bottom: unset;
  `};
`;

const Caption = styled.div`
  max-width: 508px;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 140%;
  text-align: center;
  letter-spacing: 0.0015em;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  color: ${color.white};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  ${media.tablet`
    max-width: 276px;
    max-height: 41px;
    font-size: 14px;
    letter-spacing: 0.001em;
  `};
  ${media.mobile`
    max-height: 19px;
    -webkit-line-clamp: 1;
  `}
`;

const ProIcon = styled.img`
  margin-top: 3px;
  flex: none;
  order: 0;
  flex-grow: 0;
  width: 24px;
  height: 24px;
  ${media.tablet`
    margin-top: 1px;
    width: 16px;
    height: 16px;
  `};
`;

const MetaWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px 20px;
  gap: 20px;

  position: absolute;
  height: 48px;
  left: 0px;
  right: 0px;
  bottom: 16px;
  ${media.tablet`
    padding: 8px 20px;
    height: 64px;
    bottom: 0px;
    gap: 12px;
  `};
  ${media.mobile`
    padding: 8px 16px;
    width: 320px;
    left: calc(50% - 320px / 2);
    top: calc(50% - 64px / 2 + 88px);
  `}
`;

const Meta = styled.div.attrs(({ isLeft }) => ({
  style: {
    order: isLeft ? 0 : 2,
  },
}))`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 4px;
  width: 32px;
  height: 48px;
  opacity: 0.5;
  flex: none;
  flex-grow: 0;
`;

const MetaIcon = styled.img`
  width: 32px;
`;

const Amount = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1;
  color: ${color.white};
  font-size: 12px;
  text-align: center;
`;

const ActionButtonWrapper = styled.div`
  min-width: 160px;
  height: 36px;
  font-size: 20px;
  ${media.tablet`
    font-size: 18px;
  `}
`;

const FilledButton = css`
  background-color: ${textColor.white};
`;

const OutlineButton = css`
  background-color: rgba(71, 71, 71, 0.3);
  border: 1px solid ${color.tealBlue};
`;

const ActionButton = styled(({ hasMessageId, ...restProps }) =>
  hasMessageId ? <Link {...restProps} /> : <div {...restProps} />
)`
  ${ResetButtonStyle}
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 8px;
  gap: 8px;
  width: 320px;
  height: 40px;
  border-radius: 4px;
  ${props => (props.$isUnlocked ? FilledButton : OutlineButton)}
  ${media.tablet`
    padding: 8px;
    gap: 4px;
    width: 272px;
    height: 36px;
    flex-grow: 1;
  `};
  ${media.mobile`
    width: 192px;
  `};
`;

const UnlockActionButton = styled(ActionButton)`
  color: ${textColor.primary};
  background-color: rgba(100, 100, 100, 0.3);
`;

const PlayActionButton = styled(ActionButton)`
  color: ${color.black};
  background-color: ${color.white};
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 25.88%,
    rgba(0, 0, 0, 0.8) 100%
  );
`;

const PlayIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const DiamondIcon = styled.img`
  width: 24px;
  height: 24px;
  ${media.tablet`
    width: 20px;
    height: 20px;
  `};
`;

const UnlockPrice = styled.span`
  font-size: 22px;
  ${media.tablet`
    font-size: 20px;
  `};
`;

const Picture = styled.picture`
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export default withSentryEndSpan(withViewportItemTracker(HomeCoverMessage));
